import React, { useState, useEffect, useMemo } from "react";
import axios from "../axios/axios";
import {
  Box,
  Stack,
  Table,
  Text,
  Thead,
  Tbody,
  Th,
  Tr,
  TableContainer,
  Td,
  Skeleton,
  Input,
  Button,
  Select,
  Center,
  Switch,
  SimpleGrid,
} from "@chakra-ui/react";
import { Link, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import formatHoursAndMinutes from "../lib/helpers/formatHoursAndMinutes";

const MONTH_ORDER = {
  janvier: 1,
  fevrier: 2,
  février: 2,
  mars: 3,
  avril: 4,
  mai: 5,
  juin: 6,
  juillet: 7,
  aout: 8,
  août: 8,
  septembre: 9,
  octobre: 10,
  novembre: 11,
  decembre: 12,
  décembre: 12,
};

function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function sectionSortKey(name = "") {
  const normalized = normalizeText(name);
  const yearMatch = normalized.match(/\b(20\d{2})\b/);
  const monthEntry = Object.entries(MONTH_ORDER).find(([month]) =>
    normalized.includes(normalizeText(month))
  );

  if (yearMatch && monthEntry) {
    return {
      group: 0,
      year: Number(yearMatch[1]),
      month: monthEntry[1],
      text: normalized,
    };
  }

  return {
    group: 1,
    year: 9999,
    month: 99,
    text: normalized,
  };
}

function compareSections(a, b) {
  const keyA = sectionSortKey(a.nom);
  const keyB = sectionSortKey(b.nom);

  return keyA.group - keyB.group ||
    keyA.year - keyB.year ||
    keyA.month - keyB.month ||
    keyA.text.localeCompare(keyB.text, "fr", {numeric: true, sensitivity: "base"});
}

function getLastFiscalYearRange(date = new Date()) {
  const year = date.getFullYear();
  const fiscalYearEnd = new Date(year, 2, 31);
  const endYear = date > fiscalYearEnd ? year : year - 1;

  return {
    start_date: `${endYear - 1}-04-01`,
    end_date: `${endYear}-03-31`,
  };
}

export default function AdminProjectsContainer() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(
    () => Number(searchParams.get("page")) || 1,
    [searchParams]
  );

  const q = useMemo(() => searchParams.get("q") || "", [searchParams]);
  const id_client = useMemo(
    () => searchParams.get("id_client") || "",
    [searchParams]
  );
  const user_id = useMemo(
    () => searchParams.get("user_id") || "",
    [searchParams]
  );

  // false par defaut : afficher les sections meme si le temps Asana vaut 0/null
  const only_with_time = useMemo(
    () => searchParams.get("only_with_time") || "false",
    [searchParams]
  );

  const start_date = useMemo(
    () => searchParams.get("start_date") || "",
    [searchParams]
  );
  const end_date = useMemo(
    () => searchParams.get("end_date") || "",
    [searchParams]
  );

  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const selectedClient = useMemo(
    () => clients.find(client => String(client.id) === String(id_client)),
    [clients, id_client]
  );

  const isClientScoped = Boolean(id_client);

  const isValidDateRange = useMemo(() => {
    if (!start_date || !end_date) return true;
    return new Date(start_date) <= new Date(end_date);
  }, [start_date, end_date]);

  // -------------------------
  // Helpers searchParams
  // -------------------------
  const updateSearchParams = updates => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });

      // à chaque changement de filtre (hors page), on revient page 1
      if (!("page" in updates)) next.set("page", "1");

      return next;
    });
  };

  // -------------------------
  // Fetch initial lists (users, clients)
  // -------------------------
  useEffect(() => {
    axios
      .get("/users")
      .then(response => setUsers(response.data))
      .catch(error => console.error("Error fetching users:", error));

    axios
      .get("/allClients")
      .then(response => setClients(response.data))
      .catch(error => console.error("Error fetching clients:", error));
  }, []);

  // -------------------------
  // Fetch projects/sections list
  // -------------------------
  useEffect(() => {
    const queryParams = [];

    if (page) queryParams.push(`page=${page}`);
    if (q) queryParams.push(`q=${encodeURIComponent(q)}`);
    if (id_client) queryParams.push(`id_client=${encodeURIComponent(id_client)}`);
    if (user_id) queryParams.push(`user_id=${encodeURIComponent(user_id)}`);
    if (only_with_time === "true") queryParams.push(`only_with_time=true`);
    if (start_date) queryParams.push(`start_date=${encodeURIComponent(start_date)}`);
    if (end_date) queryParams.push(`end_date=${encodeURIComponent(end_date)}`);

    setLoading(true);

    axios
      .get("/projects?" + queryParams.join("&"))
      .then(response => {
        setProjects([...(response.data.projects || [])].sort(compareSections));
        setTotalPages(response.data.total_pages || 1);
        setTotalTimeSpent(Number(response.data.total_time_spent || 0));
      })
      .catch(error => {
        console.error("Error fetching projects:", error);
        setTotalTimeSpent(0);
      })
      .finally(() => setLoading(false));
  }, [page, q, id_client, user_id, only_with_time, start_date, end_date]);

  // -------------------------
  // Total time (minutes) across the full filtered result set.
  // -------------------------
  const totalTimeHoursDisplay = useMemo(() => {
    return formatHoursAndMinutes(totalTimeSpent / 60);
  }, [totalTimeSpent]);

  const clearFilters = () => {
    setSearchParams(new URLSearchParams({ page: "1" }));
  };

  const fiscalYearRange = useMemo(() => getLastFiscalYearRange(), []);

  const applyFiscalYearFilter = () => {
    updateSearchParams(fiscalYearRange);
  };

  return (
    <Box width={"full"} ml={{ base: 0, md: "20px" }} px={{ base: 2, md: 0 }}>
      <Header fontSize={{ base: "xl", md: "2xl" }}>
        Sections (Projets)
      </Header>

      {isClientScoped && (
        <Text mt={2} ml="12px" fontWeight="bold">
          {selectedClient?.client_name || projects[0]?.client_name || "Client selectionne"}
          {(selectedClient?.portfolio_name_asana || projects[0]?.portfolio_name_asana) && (
            <Text as="span" fontWeight="normal" fontStyle="italic" fontSize="sm">
              {" "}({selectedClient?.portfolio_name_asana || projects[0]?.portfolio_name_asana})
            </Text>
          )}
        </Text>
      )}

      <Box width={"auto"}>
        <Stack
          direction={{ base: "column", md: "row" }}
          width={"full"}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "start" }}
          my={5}
          spacing={{ base: 4, md: 6 }}
        >
          {/* ------------------ TABLE ------------------ */}
          <Stack direction={"column"} flex="1" minW={0}>
            <TableContainer width={"full"} overflowX="auto">
              <Table variant="simple" width={"full"} size={{ base: "sm", md: "md" }} sx={{ tableLayout: "fixed" }}>
                <Thead>
                  <Tr>
                    {!isClientScoped && <Th width="34%">Client</Th>}
                    <Th>Section</Th>
                    <Th width={{ base: "120px", md: "150px" }} isNumeric>Temps réalisé</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {loading ? (
                    <>
                      {[...Array(6)].map((_, i) => (
                        <Tr key={i}>
                          <Skeleton as={Td} height="20px" />
                          <Skeleton as={Td} height="20px" />
                          <Skeleton as={Td} height="20px" />
                        </Tr>
                      ))}
                    </>
                  ) : projects.length === 0 ? (
                    <Tr>
                      <Td colSpan={isClientScoped ? 2 : 3}>
                        <Text textAlign="center" py={4}>
                          Aucun résultat trouvé
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    projects.map(project => (
                      <Tr
                        key={project.id}
                        cursor="pointer"
                        transition="background-color 0.15s ease, color 0.15s ease"
                        _hover={{ bg: "blue.50" }}
                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                      >
                        {!isClientScoped && (
                          <Td overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                            {project.client_name}{" "}
                            <i style={{ fontSize: "12px" }}>
                              ({project.portfolio_name_asana ?? "Sans portefeuille"})
                            </i>
                          </Td>
                        )}

                        <Td overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          <Link
                            to={`/admin/projects/${project.id}`}
                            onClick={event => event.stopPropagation()}
                          >
                            {project.nom}
                          </Link>
                        </Td>

                        <Td isNumeric whiteSpace="nowrap">
                          {formatHoursAndMinutes(Number(project.time_spent || 0) / 60)}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Center margin={"auto"} w={"min-content"} mt={"20px"}>
              <Stack direction={"row"} spacing={3} mt={6} margin={"auto"} w={"full"}>
                {Array.from({ length: totalPages }, (_, index) => {
                  const p = index + 1;
                  const isActive = p === page;

                  // Conserve les filtres + change page
                  const nextParams = new URLSearchParams(searchParams);
                  nextParams.set("page", String(p));

                  return (
                    <Button
                      key={p}
                      variant={"outline"}
                      as={Link}
                      to={`/admin/projects?${nextParams.toString()}`}
                      px={6}
                      color={isActive ? "white" : "black"}
                      background={isActive ? "blue.900" : "transparent"}
                      _hover={{ bg: "blue.900", color: "white" }}
                    >
                      {p}
                    </Button>
                  );
                })}
              </Stack>
            </Center>
          </Stack>

          {/* ------------------ FILTERS PANEL ------------------ */}
          <Stack direction={"column"} width={{ base: "full", md: "320px", lg: "360px" }} flexShrink={0}>
            <Text fontSize="2xl" fontWeight="bold">
              Total d’heures :{" "}
              {loading ? (
                <Skeleton as="span" display="inline-block" width="100px" height="30px" />
              ) : (
                totalTimeHoursDisplay
              )}
            </Text>

            <Switch
              mb={4}
              isChecked={only_with_time === "true"}
              onChange={e =>
                updateSearchParams({ only_with_time: e.target.checked ? "true" : "false" })
              }
            >
              Seulement les sections avec du temps
            </Switch>

            <Button variant="outline" onClick={clearFilters}>
              Réinitialiser les filtres
            </Button>

            <Text fontSize={"md"} fontWeight={"bold"} mt={3}>
              Filtrer par client
            </Text>
            <Select
              placeholder="Tous les clients"
              value={id_client}
              onChange={e => updateSearchParams({ id_client: e.target.value })}
            >
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.client_name}
                </option>
              ))}
            </Select>

            <Text fontSize={"md"} fontWeight={"bold"}>
              Filtrer par nom/code de section
            </Text>
            <Input
              type="text"
              placeholder="Hopopup"
              value={q}
              onChange={e => updateSearchParams({ q: e.target.value })}
            />

            <Text fontSize={"md"} fontWeight={"bold"}>
              Filtrer par Collaborateur
            </Text>
            <Select
              placeholder="Tous les Collaborateurs"
              value={user_id}
              onChange={e => updateSearchParams({ user_id: e.target.value })}
            >
              {users
                .filter(user => user.user_gid)
                .map(user => (
                  <option key={user.user_gid} value={user.user_gid}>
                    {user.name}
                  </option>
                ))}
            </Select>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontSize={"md"} fontWeight={"bold"}>
                  Date de début
                </Text>
                <Input
                  type="date"
                  value={start_date}
                  onChange={e => updateSearchParams({ start_date: e.target.value })}
                />
              </Box>
              <Box>
                <Text fontSize={"md"} fontWeight={"bold"}>
                  Date de fin
                </Text>
                <Input
                  type="date"
                  value={end_date}
                  onChange={e => updateSearchParams({ end_date: e.target.value })}
                />
              </Box>
            </SimpleGrid>

            {!isValidDateRange && (
              <Text color="red.500">
                La date de fin doit être postérieure à la date de début.
              </Text>
            )}

            <Button variant="outline" onClick={applyFiscalYearFilter}>
              Bilan fiscal {fiscalYearRange.start_date.slice(0, 4)}-
              {fiscalYearRange.end_date.slice(0, 4)}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
